import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { 
  ArrowLeft, Image, Type, Layout, Save, 
  RotateCcw, Loader2, Eye, Upload, X, FileImage
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminBrandingSettings = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { branding: currentBranding, refreshBranding, updateBranding } = useTheme();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    portal_title: '',
    logo_url: '',
    favicon_url: '',
    footer: {
      show_footer: true,
      copyright_text: '',
      disclaimer_text: ''
    }
  });

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    loadBranding();
  }, []);

  const loadBranding = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/branding/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFormData({
          portal_title: data.portal_title || 'Marketplace',
          logo_url: data.logo_url || '',
          favicon_url: data.favicon_url || '',
          footer: {
            show_footer: data.footer?.show_footer ?? true,
            copyright_text: data.footer?.copyright_text || '© 2026 Marketplace. All rights reserved.',
            disclaimer_text: data.footer?.disclaimer_text || 'Authorized personnel only.'
          }
        });
      }
    } catch (error) {
      console.error('Failed to load branding:', error);
      toast.error('Failed to load branding settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!isSuperAdmin) {
      toast.error('Only Super Admin can update branding settings');
      return;
    }

    setSaving(true);
    try {
      // Keep the default colors - only save identity and footer settings
      const saveData = {
        ...formData,
        colors: {
          primary: '#3B82F6',
          secondary: '#1E293B',
          accent: '#F59E0B',
          text_primary: '#111827'
        }
      };

      const res = await fetch(`${API_URL}/api/branding/`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(saveData)
      });

      if (res.ok) {
        toast.success('Branding settings saved successfully');
        refreshBranding(); // Refresh global theme
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save branding');
      }
    } catch (error) {
      toast.error('Failed to save branding settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!isSuperAdmin) {
      toast.error('Only Super Admin can reset branding settings');
      return;
    }

    if (!confirm('Reset all branding to defaults? This cannot be undone.')) return;

    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/branding/reset`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Branding reset to defaults');
        loadBranding();
        refreshBranding();
      }
    } catch (error) {
      toast.error('Failed to reset branding');
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (field, value) => {
    const newData = { ...formData, [field]: value };
    setFormData(newData);
    // Apply live preview
    updateBranding(newData);
  };

  const handleFooterChange = (field, value) => {
    setFormData({ 
      ...formData, 
      footer: { ...formData.footer, [field]: value }
    });
  };

  if (loading) {
    if (embedded) {
      return (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      );
    }
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </DashboardLayout>
    );
  }

  const content = (
    <div className={embedded ? "" : "p-6 max-w-4xl mx-auto"} data-testid="branding-settings-page">
      {/* Header - hide back button when embedded */}
      {!embedded && (
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Look & Feel</h1>
          <p className="text-gray-600 mt-1">
            Customize your marketplace branding - logo and name
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleReset} disabled={saving || !isSuperAdmin}>
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset to Defaults
          </Button>
          <Button onClick={handleSave} disabled={saving || !isSuperAdmin}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>

      {!isSuperAdmin && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800">
          <strong>View Only:</strong> Only Super Admin can modify branding settings.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Settings Panel */}
        <div className="lg:col-span-2 space-y-6">
          {/* Brand Identity */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Type className="h-5 w-5" />
                Brand Identity
              </CardTitle>
              <CardDescription>
                Configure your marketplace name and logo. These appear in the sidebar, login page, and browser tab.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="portal_title">Marketplace Name *</Label>
                <Input
                  id="portal_title"
                  value={formData.portal_title}
                  onChange={(e) => handleFieldChange('portal_title', e.target.value)}
                  placeholder="My Marketplace"
                  disabled={!isSuperAdmin}
                />
                <p className="text-xs text-gray-500">
                  Appears in browser tab, sidebar header, and login page
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="logo_url">Logo URL</Label>
                <div className="flex gap-2">
                  <Input
                    id="logo_url"
                    value={formData.logo_url}
                    onChange={(e) => handleFieldChange('logo_url', e.target.value)}
                    placeholder="https://example.com/logo.png"
                    disabled={!isSuperAdmin}
                    className="flex-1"
                  />
                  {formData.logo_url && (
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleFieldChange('logo_url', '')}
                      disabled={!isSuperAdmin}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                {formData.logo_url && (
                  <div className="mt-2 p-4 bg-gray-50 rounded-lg flex items-center justify-center">
                    <img 
                      src={formData.logo_url} 
                      alt="Logo preview" 
                      className="max-w-[200px] max-h-[100px] object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <p className="text-sm text-red-500 hidden">Failed to load image</p>
                  </div>
                )}
                <p className="text-xs text-gray-500">
                  Recommended: PNG or SVG, at least 200x200px. Leave empty for text-only header.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="favicon_url">Favicon URL (Browser Tab Icon)</Label>
                <div className="flex gap-2">
                  <Input
                    id="favicon_url"
                    value={formData.favicon_url}
                    onChange={(e) => handleFieldChange('favicon_url', e.target.value)}
                    placeholder="https://example.com/favicon.ico"
                    disabled={!isSuperAdmin}
                    className="flex-1"
                  />
                  {formData.favicon_url && (
                    <div className="flex-shrink-0 w-10 h-10 border rounded flex items-center justify-center overflow-hidden bg-gray-50">
                      <img 
                        src={formData.favicon_url} 
                        alt="Favicon preview" 
                        className="max-w-full max-h-full"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500">
                  32x32px .ico or .png file
                </p>
              </div>

              {/* Invoice Header Section */}
              <div className="space-y-2">
                <Label htmlFor="invoice_header_url" className="flex items-center gap-2">
                  <FileImage className="h-4 w-4" />
                  Invoice Header Image
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="invoice_header_url"
                    value={formData.invoice_header_url || ''}
                    onChange={(e) => handleFieldChange('invoice_header_url', e.target.value)}
                    placeholder="https://example.com/invoice-header.png"
                    disabled={!isSuperAdmin}
                    className="flex-1"
                  />
                  {formData.invoice_header_url && (
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleFieldChange('invoice_header_url', '')}
                      disabled={!isSuperAdmin}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                {formData.invoice_header_url && (
                  <div className="mt-2 p-4 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-2">Preview (Invoice Header)</p>
                    <img 
                      src={formData.invoice_header_url} 
                      alt="Invoice header preview" 
                      className="max-w-full max-h-[120px] object-contain border rounded"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <p className="text-sm text-red-500 hidden">Failed to load image</p>
                  </div>
                )}
                <p className="text-xs text-gray-500">
                  This image appears at the top of vendor invoices. Recommended: PNG, 800x150px or similar aspect ratio.
                  Vendors can download this from Pack & Ship section.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Footer Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Layout className="h-5 w-5" />
                Footer Configuration
              </CardTitle>
              <CardDescription>
                Customize the portal footer content
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="show_footer">Show Footer</Label>
                  <p className="text-xs text-gray-500 mt-1">
                    Display footer at the bottom of all pages
                  </p>
                </div>
                <Switch
                  id="show_footer"
                  checked={formData.footer.show_footer}
                  onCheckedChange={(checked) => handleFooterChange('show_footer', checked)}
                  disabled={!isSuperAdmin}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="copyright_text">Copyright Text</Label>
                <Textarea
                  id="copyright_text"
                  value={formData.footer.copyright_text}
                  onChange={(e) => handleFooterChange('copyright_text', e.target.value)}
                  placeholder="© 2026 Your Company. All rights reserved."
                  rows={2}
                  disabled={!isSuperAdmin}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="disclaimer_text">Disclaimer Text</Label>
                <Textarea
                  id="disclaimer_text"
                  value={formData.footer.disclaimer_text}
                  onChange={(e) => handleFooterChange('disclaimer_text', e.target.value)}
                  placeholder="Authorized personnel only."
                  rows={2}
                  disabled={!isSuperAdmin}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Live Preview Panel */}
        <div className="lg:col-span-1">
          <Card className="sticky top-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-4 w-4" />
                Live Preview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Sidebar Header Preview */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="p-4 bg-white border-b">
                    <p className="text-xs text-gray-500 mb-2 font-medium">Sidebar Header</p>
                    <div className="flex items-center gap-3">
                      {formData.logo_url ? (
                        <img 
                          src={formData.logo_url} 
                          alt="Logo" 
                          className="w-10 h-10 object-contain rounded"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      ) : (
                        <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center">
                          <Image className="w-5 h-5 text-gray-400" />
                        </div>
                      )}
                      <div>
                        <span className="font-bold text-gray-900 text-lg">
                          {formData.portal_title || 'Marketplace'}
                        </span>
                        <p className="text-sm text-gray-600">Admin Portal</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Login Page Preview */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-100">
                    <p className="text-xs text-gray-500 mb-2 font-medium">Login Page</p>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="flex flex-col items-center gap-2">
                        {formData.logo_url ? (
                          <img 
                            src={formData.logo_url} 
                            alt="Logo" 
                            className="w-12 h-12 object-contain"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        ) : (
                          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                            <Image className="w-6 h-6 text-blue-600" />
                          </div>
                        )}
                        <span className="font-semibold text-gray-900">
                          {formData.portal_title || 'Marketplace'}
                        </span>
                        <span className="text-xs text-gray-500">Vendor Portal</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Browser Tab Preview */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="p-4 bg-gray-100">
                    <p className="text-xs text-gray-500 mb-2 font-medium">Browser Tab</p>
                    <div className="bg-gray-200 rounded-t-lg px-3 py-2 flex items-center gap-2 max-w-[200px]">
                      {formData.favicon_url ? (
                        <img 
                          src={formData.favicon_url} 
                          alt="Favicon" 
                          className="w-4 h-4"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      ) : (
                        <div className="w-4 h-4 bg-gray-400 rounded-sm" />
                      )}
                      <span className="text-xs truncate text-gray-700">
                        {formData.portal_title || 'Marketplace'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Footer Preview */}
                {formData.footer.show_footer && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="p-4 bg-white">
                      <p className="text-xs text-gray-500 mb-2 font-medium">Footer</p>
                      <div className="border-t pt-3 text-xs text-gray-500">
                        <p>{formData.footer.copyright_text}</p>
                        <p className="mt-1">{formData.footer.disclaimer_text}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );

  // Return with or without DashboardLayout based on embedded prop
  if (embedded) {
    return content;
  }

  return (
    <DashboardLayout>
      {content}
    </DashboardLayout>
  );
};

export default AdminBrandingSettings;
